import { KeyRound } from "lucide-react"
import { useEffect, useState } from "react"
import { type AdminKeyKind, adminKeys } from "../../search-standalone/services/adminKeys"
import { CLERK_ENABLED, ClerkAccount } from "./AdminSignIn"

/** The current key of one kind, re-rendering when it is entered or forgotten. */
export function useAdminKey(kind: AdminKeyKind): string {
	const [value, setValue] = useState(() => adminKeys.get(kind))
	useEffect(() => adminKeys.subscribe(() => setValue(adminKeys.get(kind))), [kind])
	return value
}

const LABELS: Record<AdminKeyKind, { name: string; help: string }> = {
	operator: { name: "Operator key", help: "Bootstrap, delete and list stores" },
	admin: { name: "Store admin key", help: "Change settings and publish" },
}

/**
 * How an admin proves who they are: "Sign in with in5" (Clerk) when it's configured, with the
 * server's keys as a fallback. Keys are kept for this tab only and are never part of the page's code.
 */
export function AdminKeysPanel({ kinds = ["operator", "admin"] }: { kinds?: AdminKeyKind[] }) {
	const [, rerender] = useState(0)
	const [drafts, setDrafts] = useState<Record<string, string>>({})
	useEffect(() => adminKeys.subscribe(() => rerender((n) => n + 1)), [])
	const missing = kinds.filter((k) => !adminKeys.get(k))

	const keys = (
		<>
			<div className="flex items-center gap-2 mb-2">
				<KeyRound className="w-4 h-4" />
				<span className="text-xs text-muted-foreground">
					{missing.length ? "Kept for this tab only." : "Keys entered for this tab."}
				</span>
				{!missing.length && (
					<button className="ml-auto text-xs underline" onClick={() => adminKeys.clear()}>
						Forget keys
					</button>
				)}
			</div>
			{missing.length > 0 && (
				<form
					className="flex flex-wrap items-end gap-2"
					onSubmit={(e) => {
						e.preventDefault()
						for (const k of missing) if (drafts[k]) adminKeys.set(k, drafts[k])
						setDrafts({})
					}}>
					{missing.map((k) => (
						<label className="flex flex-col text-xs gap-1" key={k}>
							<span>
								{LABELS[k].name} <span className="text-muted-foreground">· {LABELS[k].help}</span>
							</span>
							<input
								aria-label={LABELS[k].name}
								autoComplete="off"
								className="border border-border rounded px-2 py-1 bg-background text-sm w-64"
								onChange={(e) => setDrafts((d) => ({ ...d, [k]: e.target.value }))}
								type="password"
								value={drafts[k] || ""}
							/>
						</label>
					))}
					<button className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm" type="submit">
						Use keys
					</button>
				</form>
			)}
		</>
	)

	return (
		<section className="border border-border rounded-lg p-4 mb-6" aria-label="Admin access">
			<h2 className="font-semibold text-sm mb-2">Admin access</h2>
			{CLERK_ENABLED ? (
				<>
					<ClerkAccount />
					<details className="mt-3" open={!missing.length}>
						<summary className="text-xs text-muted-foreground cursor-pointer">Use admin keys instead</summary>
						<div className="mt-2">{keys}</div>
					</details>
				</>
			) : (
				keys
			)}
		</section>
	)
}
